// BUG-098 — the staged set's NET effect on daily spend, for the tray's foot and the shell pill (w07 ①, m06 ③).
//
// ENGINE §11 computes `deltaAllowedPerDay` off the overlaid owners, never by adding rows up, so the figure is the
// server's (`previewChanges`, API §6.5) — the same answer the review screen reads, through the same query
// (`queries.changePreview` with no item ids: the whole set, the key the tray's own Review opens with, so the
// two share one cache entry). It is never asked for on an empty set, and the `changes` family's invalidation
// (`INVALIDATION.stageChange` and its kin) is what refreshes it when the set moves. On the demo it is an
// in-memory computation; on a live account the sandbox's — no Graph call either way (D-1266).
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFormatter, type Money } from '@sl/core';
import { useShell } from '../shell/shellContext';
import { useStaged } from './stagedContext';

export interface StagedNet {
  /** The change in allowed daily spend, in account currency — negative when the set spends less. */
  amount: Money;
  /** The figure as a signed headline amount: "−$17", "+$5". */
  text: string;
}

export function useStagedNet(): StagedNet | null {
  const { account, queries, locale, deviceTimeZone } = useShell();
  const { count } = useStaged();
  const query = useQuery({
    ...queries.changePreview(account.id),
    enabled: count > 0,
  });
  const fmt = useMemo(
    () =>
      createFormatter({
        account: { currency: account.currency, timeZone: account.timezone },
        locale,
        deviceTimeZone,
      }),
    [account.currency, account.timezone, locale, deviceTimeZone],
  );
  const amount = count > 0 ? (query.data?.data.summary.deltaAllowedPerDay ?? null) : null;
  if (amount === null) return null;
  const sign = amount > 0 ? '+' : amount < 0 ? '−' : '';
  return { amount, text: `${sign}${fmt(Math.abs(amount), 'money.headline')}` };
}
